import React from 'react';

/**
 * Linear wizard step indicator. Used by cycle creation wizards (per-client
 * and template). Visible at the top of the dialog so the coach knows where
 * they are in the flow.
 *
 * Design handoff §4.2 / §4.3.
 */
export interface StepperProps {
  steps: Array<{ key: string; label: string }>;
  /** Index of the currently active step (0-based). */
  current: number;
  /** Optional click handler — when present, lets the user jump back to a previously completed step. */
  onStepClick?: (index: number) => void;
}

const Stepper: React.FC<StepperProps> = ({ steps, current, onStepClick }) => {
  return (
    <div className="et-stepper" role="navigation" aria-label="Wizard steps">
      {steps.map((step, idx) => {
        const isActive = idx === current;
        const isDone = idx < current;
        const isClickable = !!onStepClick && idx < current;
        const handleClick = isClickable ? () => onStepClick!(idx) : undefined;
        return (
          <React.Fragment key={step.key}>
            <button
              type="button"
              className={`et-stepper__step${isActive ? ' is-active' : ''}${isDone ? ' is-done' : ''}`}
              onClick={handleClick}
              disabled={!isClickable}
              aria-current={isActive ? 'step' : undefined}
            >
              <span className="et-stepper__circle" aria-hidden>
                {isDone ? <i className="pi pi-check" /> : idx + 1}
              </span>
              <span className="et-stepper__label">{step.label}</span>
            </button>
            {idx < steps.length - 1 && (
              <span className={`et-stepper__line${idx < current ? ' is-done' : ''}`} aria-hidden />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default Stepper;
